import type { InvoiceDetailResponse } from '../../../types/invoice';
import type { NexDetailLine } from '../../../types/invoice/nex';
import { invoiceHasNexDuplicate406Error } from '../../../utils/invoiceNexDuplicate406';
import { evaluateNexLineMatchSubmitForUi } from '../../../utils/nexLineMatchSubmitGuard';
import type { ReturnSlipSubmitPreviewResult } from '../../../utils/returnSlipSubmitPreview';
import type { InvoiceNexIdentifierUi } from '../../../utils/invoiceNexIdentifierUi';
import type { UnmatchedLineItemsNexSubmitMode } from '../../../utils/unmatchedLineItemsNexSubmitMode';
import { allowsManualUnmatchedNexSubmit } from '../../../utils/unmatchedLineItemsNexSubmitMode';

export interface InvoiceDetailSubmitGating {
    missingRequiredNexMatchesForSubmit: boolean;
    missingNexMatchSubmitHelpText: string | undefined;
    returnSlipSubmitBlocked: boolean;
    locationBlocksSubmit: boolean;
    arithmeticBlocksSubmit: boolean;
    duplicate406BlocksSubmit: boolean;
    nexMatchBlocksSubmit: boolean;
    submitDisabledHelpCombined: string | undefined;
    handleSubmitWithArithmeticGuard: (openArithmeticBlock: () => void, onSubmitClick: () => void) => void;
}

export function computeInvoiceDetailSubmitGating(args: {
    invoice: InvoiceDetailResponse;
    isReturnSlip: boolean;
    hasArithmeticFlags: boolean;
    returnSlipSubmitReady: boolean;
    returnSlipSubmitPreview: ReturnSlipSubmitPreviewResult | null;
    identifierUi: InvoiceNexIdentifierUi;
    submitArithmeticHelpText: string | undefined;
    regularLineItems: InvoiceDetailResponse['lineItems'];
    freightLineItems: InvoiceDetailResponse['freightItems'];
    fuelLineItems: InvoiceDetailResponse['fuelItems'];
    effectiveMatchedNexLines: Array<{ partIndex: number; nexLine: NexDetailLine }>;
    localMatchedNexOverrides: Record<number, NexDetailLine>;
    unmatchedPartIndices: Set<number>;
    remapSubmitPreviewPartIndex: (oldIndex: number) => number | undefined;
    /** Saved or unsaved shop location selection in Key Identifiers. */
    effectiveLocationId?: string;
    unmatchedLineItemsNexSubmitMode?: UnmatchedLineItemsNexSubmitMode;
}): Omit<InvoiceDetailSubmitGating, 'handleSubmitWithArithmeticGuard'> {
    const {
        invoice,
        isReturnSlip,
        hasArithmeticFlags,
        returnSlipSubmitReady,
        returnSlipSubmitPreview,
        identifierUi,
        submitArithmeticHelpText,
        regularLineItems,
        freightLineItems,
        fuelLineItems,
        effectiveMatchedNexLines,
        localMatchedNexOverrides,
        unmatchedPartIndices,
        remapSubmitPreviewPartIndex,
        effectiveLocationId,
        unmatchedLineItemsNexSubmitMode = 'dont_allow',
    } = args;

    const regularRequiredCount = regularLineItems?.length ?? 0;
    const freightRequiredCount = freightLineItems?.length ?? 0;
    const fuelRequiredCount = fuelLineItems?.length ?? 0;

    const matchedSubmitIndices = (() => {
        const matched = new Set<number>();
        for (const entry of effectiveMatchedNexLines ?? []) {
            const raw = entry?.partIndex;
            if (typeof raw !== 'number' || !Number.isFinite(raw)) continue;
            const mapped = remapSubmitPreviewPartIndex(raw);
            if (typeof mapped === 'number' && Number.isFinite(mapped)) matched.add(mapped);
        }
        for (const key of Object.keys(localMatchedNexOverrides ?? {})) {
            const idx = Number.parseInt(key, 10);
            if (Number.isFinite(idx)) matched.add(idx);
        }
        return matched;
    })();

    const matchEvaluation = evaluateNexLineMatchSubmitForUi({
        lineItems: regularLineItems ?? [],
        freightItems: freightLineItems ?? [],
        fuelItems: fuelLineItems ?? [],
        matchedPartIndices: matchedSubmitIndices,
        unmatchedPartIndices: unmatchedPartIndices ?? new Set<number>(),
        unmatchedLineItemsNexSubmitMode,
    });

    const missingSubmitMatchCounts = (() => {
        const regularCount = regularLineItems?.length ?? 0;
        const freightCount = freightLineItems?.length ?? 0;
        const missing = { regular: 0, freight: 0, fuel: 0 };

        for (const idx of matchEvaluation.unmatchedBillablePartIndices) {
            if (idx < regularCount) missing.regular += 1;
            else if (idx < regularCount + freightCount) missing.freight += 1;
            else missing.fuel += 1;
        }
        return missing;
    })();

    const missingRequiredNexMatchesForSubmit = matchEvaluation.blockSubmit;

    const missingNexMatchSubmitHelpText = missingRequiredNexMatchesForSubmit
        ? matchEvaluation.blockReason === 'all_unmatched'
            ? [
                  'Submit is disabled until at least one billable line item is matched to a NEX PO line.',
                  'Match or reconcile at least one regular, freight, or fuel row, then submit again.',
              ].join('\n\n')
            : [
                  'Submit is disabled until all required items are matched to NEX lines.',
                  `Missing matches: ${missingSubmitMatchCounts.regular}/${regularRequiredCount} regular line items, ${missingSubmitMatchCounts.freight}/${freightRequiredCount} freight line items, ${missingSubmitMatchCounts.fuel}/${fuelRequiredCount} fuel line items.`,
                  'Match each missing row from the line-item reconciliation controls, then submit.',
              ].join('\n\n')
        : allowsManualUnmatchedNexSubmit(unmatchedLineItemsNexSubmitMode) &&
            matchEvaluation.unmatchedBillablePartIndices.length > 0
          ? [
                `${matchEvaluation.unmatchedBillablePartIndices.length} billable line item(s) will submit as manual NEX lines.`,
                'Unmatched lines are sent with PO header 0, PO line number 0, and manualLineInd 1.',
            ].join('\n\n')
          : undefined;

    const returnSlipSubmitBlocked = isReturnSlip && !returnSlipSubmitReady;
    const arithmeticBlocksSubmit = !isReturnSlip && hasArithmeticFlags;
    const duplicate406BlocksSubmit = invoiceHasNexDuplicate406Error(invoice);
    const locationBlocksSubmit = !String(effectiveLocationId ?? invoice.locationId ?? '').trim();
    const nexMatchBlocksSubmit = !isReturnSlip && missingRequiredNexMatchesForSubmit;

    const submitDisabledHelpCombined = invoiceHasNexDuplicate406Error(invoice)
        ? 'NEX returned HTTP 406 (often a duplicate submission or another business rule). This invoice cannot be submitted a second time.'
        : locationBlocksSubmit
          ? 'Select a shop location in Key Identifiers before submitting.'
          : returnSlipSubmitBlocked
          ? returnSlipSubmitPreview && !returnSlipSubmitPreview.ok
              ? returnSlipSubmitPreview.reason
              : `Enter a valid ${identifierUi.returnSlipSubmitReferencePhrase} and match every returned item to a NEX return line. Use the light bulb on each returned item row to open reconciliation and select the correct NEX /returns line, then confirm/save if needed.`
          : nexMatchBlocksSubmit
            ? missingNexMatchSubmitHelpText
            : submitArithmeticHelpText;

    return {
        missingRequiredNexMatchesForSubmit,
        missingNexMatchSubmitHelpText,
        returnSlipSubmitBlocked,
        locationBlocksSubmit,
        arithmeticBlocksSubmit,
        duplicate406BlocksSubmit,
        nexMatchBlocksSubmit,
        submitDisabledHelpCombined,
    };
}

export function createSubmitWithArithmeticGuard(
    isReturnSlip: boolean,
    hasArithmeticFlags: boolean,
): InvoiceDetailSubmitGating['handleSubmitWithArithmeticGuard'] {
    return (openArithmeticBlock, onSubmitClick) => {
        if (!isReturnSlip && hasArithmeticFlags) {
            openArithmeticBlock();
            return;
        }
        onSubmitClick();
    };
}
